import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input, OnInit, ViewContainerRef } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators, } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { getCountryCallingCode, parse } from 'libphonenumber-js';
import { BehaviorSubject, catchError, combineLatest, concat, debounceTime, distinctUntilChanged, EMPTY, filter, finalize, forkJoin, map, Observable, of, shareReplay, skipUntil, startWith, Subscription, switchMap, take, tap, throttle, timer, withLatestFrom } from 'rxjs';

import { Location } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { CompanyDetails } from 'src/app/dashboard/models/companyDetails.model';
import { InvestingEntityOwnershipReqRes } from 'src/app/dashboard/models/InvestingEntityOwnershipReqRes.model';
import { GpHoldingListItemReqRes } from 'src/app/dashboard/shared/holding/GpHoldingListItem.model';
import { DialogService } from 'src/app/services/dialog.service';
import { ResourceService } from 'src/app/services/resource.service';
import { RoutingService } from 'src/app/services/routing.service';
import { UnitBankApplicationDataService } from 'src/app/services/shared/unit-bank-application-data.service';
import { UnitBankApplicationService } from 'src/app/services/shared/unit-bank-application.service';
import { UserService } from 'src/app/services/shared/user.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { UrlUtilsService } from 'src/app/services/url-utils.service';
import { UtilsService } from 'src/app/services/utils.service';
import { AddressFields, AddressFormSettings } from 'src/app/shared/components/address-form/address-form.settings';
import { ConfirmDialogParams } from 'src/app/shared/components/confirm-dialog/confirm-dialog.component';
import { BusinessEntityType } from 'src/app/shared/enums/BusinessEntityType.enum';
import { TermType } from 'src/app/shared/enums/TermsTypes.enum';
import { UnitApplicationParentType } from 'src/app/shared/enums/unit-bank-account/UnitApplicationParentType.enum';
import { UnitApplicationStatus } from 'src/app/shared/enums/unit-bank-account/UnitApplicationStatus.enum';
import { UnitBusinessAnnualRevenue } from 'src/app/shared/enums/unit-bank-account/UnitBusinessAnnualRevenue.enum';
import { UnitBusinessVertical } from 'src/app/shared/enums/unit-bank-account/UnitBusinessVertical.enum';
import { UnitNumberOfEmployees } from 'src/app/shared/enums/unit-bank-account/UnitNumberOfEmployees.enum';
import { UnitOccupation } from 'src/app/shared/enums/unit-bank-account/UnitOccupation.enum';
import { UnitRevenue } from 'src/app/shared/enums/unit-bank-account/UnitRevenue.enum';
import { UnitSourceOfIncome } from 'src/app/shared/enums/unit-bank-account/UnitSourceOfIncome.enum';
import { LoggerService } from 'src/app/shared/errors/logger.service';
import { BaseResponseDto } from 'src/app/shared/models/BaseResponseDto.model';
import { CountryModel } from 'src/app/shared/models/CountryModel';
import { LocationDetailsResponse } from 'src/app/shared/models/LocationDetailsResponse.model';
import { BasePhoneVerificationStrategy } from 'src/app/shared/models/phone-verification-strategies/base-phone-verification-strategy.model';
import { UnitApplicationValidationLevel } from 'src/app/shared/models/unit-bank-account/CreateUpdateDraftUnitApplication.model';
import { UnitAddress } from 'src/app/shared/models/unit-bank-account/UnitAddress.model';
import { UnitBeneficialOwnerReqRes } from 'src/app/shared/models/unit-bank-account/UnitBeneficialOwnerReqRes.model';
import { UnitBusinessApplicationReqRes } from 'src/app/shared/models/unit-bank-account/UnitBusinessApplicationReqRes.model';
import { UnitBusinessContact } from 'src/app/shared/models/unit-bank-account/UnitBusinessContact.model';
import { UnitFullName } from 'src/app/shared/models/unit-bank-account/UnitFullName.model';
import { UnitOfficer } from 'src/app/shared/models/unit-bank-account/UnitOfficer.model';
import { UnitPhone } from 'src/app/shared/models/unit-bank-account/UnitPhone.model';
import { TerraNumberPipe } from 'src/app/shared/pipes/TerraNumber.pipe';
import { TerraUtils } from 'src/app/shared/TerraUtils';
import { AtLeastOneStateMatcher, CustomValidators } from 'src/app/shared/validators/custom.validators';
import { UnitAccountProvider } from '../../../enums/UnitAccountProvider.enum';
import { PhoneVerificationDialogComponent } from '../../phone-verification-dialog/phone-verification-dialog.component';
import { PhoneVerificationDialogContext } from '../../phone-verification-dialog/PhoneVerificationDialogContext';

enum SsnOrPassport {
  SSN = 1,
  Passport = 2,
}

@Component({
  selector: 'terra-unit-create-edit-application',
  templateUrl: './unit-create-edit-application.component.html',
  styleUrls: [
    './unit-create-edit-application.component.scss',
    '../unit-shared.scss',
  ],
  providers: [
    UnitBankApplicationService,
    {
      provide: BasePhoneVerificationStrategy,
      useClass: UnitBankApplicationService,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitCreateEditApplicationComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly autoSavingTime = 5 * 60 * 1000;

  readonly today = new Date();

  static fullAppData: UnitBusinessApplicationReqRes;
  readonly MIN_DATE = new Date('1900-01-01');

  @Input() allHoldings$: Observable<GpHoldingListItemReqRes[]> = of([]);
  @Input() allCompanies$: Observable<CompanyDetails[]> = of([]);

  @Input() processingSubmit$ = new BehaviorSubject(false);
  @Input() currentUnitApplicationParentType: UnitApplicationParentType;

  private static _subscriptionId = 0;
  // constants
  SsnOrPassport = SsnOrPassport;
  allCountries = this.resourceService.allCountries;
  UnitedStatesCountry = this.resourceService.getCountryByCode(
    TerraUtils.consts.countryCode.US
  );
  UnitedStatesId = this.UnitedStatesCountry.id;
  termType = TermType;
  unitApplicationParentType = UnitApplicationParentType;

  allStates = this.resourceService.allStates;
  allEntityTypes = this.resourceService.allEntityTypes;
  allOfficerTitles = this.resourceService.allOfficerTitles;
  atLeastOneErrorMatcher = new AtLeastOneStateMatcher();
  generalServerErrorMessage: string =
    TerraUtils.consts.messages.GENERAL_SUBMIT_ERROR;
  serverErrorMessage: string;

  UnitBusinessVertical = UnitBusinessVertical;
  allBusinessVertical = UnitBusinessVertical.listAll();

  UnitOccupation = UnitOccupation;
  allOccupation = UnitOccupation.listAll();

  UnitRevenue = UnitRevenue;
  allRevenue = UnitRevenue.listAll();

  UnitBusinessAnnualRevenue = UnitBusinessAnnualRevenue;
  allBusinessRevenue = UnitBusinessAnnualRevenue.listAll();

  UnitNumberOfEmployees = UnitNumberOfEmployees;
  allNumberOfEmployees = UnitNumberOfEmployees.listAll();

  UnitSourceOfIncome = UnitSourceOfIncome;
  allSourceOfIncome = UnitSourceOfIncome.listAll();

  allCountriesOfOperation = this.resourceService.allCountries;

  private readonly beneficialOwnerAddressCountrySubscriptionIdProp = 'addressCountrySubscriptionId';

  private readonly unitWebsitePattern = `^(http(s)?:\\/\\/.)?(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{2,256}\\.[a-z]{2,12}\\b([-a-zA-Z0-9@:%_\\+.~#?&//=]*)$`;

  get newSubscriptionId() {
    return UnitCreateEditApplicationComponent._subscriptionId++;
  }

  subscriptions = new Map<number, Subscription>();

  /**
   * Save auto generated beneficial owners in order to avoid multiple API calls
   */
  autoGeneratedBeneficialOwnersPerHolding = new Map<number, InvestingEntityOwnershipReqRes[]>();
                                                 
  private currentApplicationId$ = new BehaviorSubject<number>(null);
  requestedApplicationId$: Observable<number> =
    this.route.params.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((params) => (params.id ? +params.id : null)),
      shareReplay(1),
      tap((appId) => this.currentApplicationId$.next(appId))
    );

  creationType$ = this.route.queryParams.pipe(
    map((params) =>
      params.type ? +params.type : this.currentUnitApplicationParentType
    ),
    shareReplay(1)
  );

  isEditMode$ = this.requestedApplicationId$.pipe(
    map((id) => coerceBooleanProperty(id)),
    shareReplay(1)
  );

  unitAccountThreadProvider$ = this.userService.accountDetails$.pipe(
    // if no organization -> then also use thread
    map((acc) => !!!acc.organizationDetails?.unitAccountProvider || (acc.organizationDetails?.unitAccountProvider === UnitAccountProvider.Thread)));

  application$ = this.requestedApplicationId$.pipe(
    takeUntilDestroyed(this.destroyRef),
    switchMap((id) =>
      id ? this.bankApplicationDataService.getById(id) : of(null)
    ),
    shareReplay(1)
  );

  applicationLevel$ = combineLatest([
    this.creationType$,
    this.application$,
  ]).pipe(
    map(([type, application]) => {
      this.currentUnitApplicationParentType =
        application?.parentType ||
        type ||
        this.currentUnitApplicationParentType;
    })
  );

  readonly contactFirstNameMaxLength = 36;
  readonly contactLastNameMaxLength = 48;

  readonly continueSendQueryParamsName = 'continueSend';

  /**
   * If we navigate to this component from a specific holding - get the holding Id from queryparams
   */
  preselectedHoldingId$ = this.route.queryParams.pipe(
    map((params) => (params.holdingId ? +params.holdingId : null)),
    shareReplay(1)
  );

  preselectedCompanyId$: Observable<string> = this.isEditMode$.pipe(
    switchMap(editMode => editMode ? this.application$.pipe(map(application => application.companyDetailsId)) : this.route.queryParams.pipe(map(params => params.companyId))),
    shareReplay(1)
  );

  organizationName$ = this.userService.accountDetails$.pipe(
    takeUntilDestroyed(this.destroyRef),
    map((x) => x.organizationDetails.name),
    shareReplay(1)
  );
  /**
   * We allow selecting any holding that doesn't already have an appication,
   * and the holding connected to the application we are currently editing.
   */
  selectableHoldings$: Observable<GpHoldingListItemReqRes[]>;

  /**
   * We don't allow selecting a holding that already have applications,
   * unless it's the holding already connected to the application we are currently editing.
   */
  disabledHoldings$: Observable<GpHoldingListItemReqRes[]>;

  isPreselectedHolding$ = this.preselectedHoldingId$.pipe(
    map((holdingId) => !!holdingId),
    shareReplay(1)
  );

  isPreselectedCompany$ = this.preselectedCompanyId$.pipe(
    map((companyId) => !!companyId),
    shareReplay(1)
  );

  preselectedHolding$: Observable<GpHoldingListItemReqRes>;
  preselectedCompany$: Observable<CompanyDetails>;


  dataLoaded$: Observable<{ x: GpHoldingListItemReqRes[] }>;

  isLoadingBeneficialOwners$ = new BehaviorSubject(false);

  /** The page from which the user arrived. To be used for cancel/close buttons. */
  originUrl: string;

  preselectedHoldingId: number = null;
  preselectedCompanyId: string = null;

  pageForm: UntypedFormGroup;

  isSelectedHoldingAllowed$: Observable<boolean>;

  selectedHoldingApplicationPageUrl$: Observable<string>;

  get beneficialOwnersFormArray(): UntypedFormArray {
    return this.pageForm?.get('beneficialOwners') as UntypedFormArray;
  }

  isSubmitAttempt$ = new BehaviorSubject(false);
  isGeneralServerError$ = new BehaviorSubject(false);
  hasServerErrorMessage$ = new BehaviorSubject(false);
  showAdditionalBusinessInformation$ = new BehaviorSubject(true);

  isSaved = false;

  addressFormSettings = this.generateAddressFormSettings();
  bankingProvider = UnitAccountProvider.Thread;
  UnitAccountProvider = UnitAccountProvider;

  get bankingProviderString(): string {
    return UnitAccountProvider.toProvidedBy(this.bankingProvider);
  }

  originalBusinessAddress$ = combineLatest([
    this.application$,
    this.userService.accountDetails$,
  ]).pipe(
    withLatestFrom(this.isEditMode$),
    map(([[application, accountDetails], isEditMode]) => {
      this.bankingProvider =
        accountDetails?.organizationDetails?.unitAccountProvider == null
          ? UnitAccountProvider.Thread
          : accountDetails?.organizationDetails?.unitAccountProvider;
      if (isEditMode) {
        return this.getFormValueFromUnitAddress(application?.address);
      } else if (
        !!accountDetails.organizationDetails?.locationDetails &&
        accountDetails.organizationDetails?.locationDetails?.countryId === 1
      ) {
        return accountDetails.organizationDetails.locationDetails;
      } else if (
        !accountDetails.locationDetails &&
        accountDetails.locationDetails?.countryId === 1
      ) {
        return accountDetails.locationDetails;
      } else {
        return {
          countryId: 1,
        } as LocationDetailsResponse;
      }
    })
  );

  originalOfficerAddress$ = this.application$.pipe(
    take(1),
    map((application) => {
      return this.getFormValueFromUnitAddress(application?.officer?.address);
    }),
    shareReplay(1)
  );

  /** An Beneficial owner Id => Beneficial Owner address mapping */
  beneficialOwnerAddressesPerOwner$ = this.application$.pipe(
    map((application) => {
      if (!application) {
        return new Map();
      }
      const arr = new Map(
        application.beneficialOwners.map((owner) => [
          owner.id,
          this.getFormValueFromUnitAddress(owner.address),
        ])
      );
      return arr;
    })
  );

  isCreateApplicationClickedBeforeRedirect = false;

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private bankApplicationDataService: UnitBankApplicationDataService,
    private unitBankApplicationService: UnitBankApplicationService,
    private resourceService: ResourceService,
    private routingService: RoutingService,
    private router: Router,
    private loggerService: LoggerService,
    private vcr: ViewContainerRef,
    private numberPipe: TerraNumberPipe,
    private titleService: Title,
    private location: Location,
    private userService: UserService,
    private utilsService: UtilsService,
    private urlUtilsService: UrlUtilsService,
    private cdr:ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    const navigationData = history.state;
    const redirectedFromHoldingId = +this.route.snapshot.queryParams?.holdingId;
    const redirectedFromCompanyId = this.route.snapshot.queryParams?.companyId;

    this.originUrl =
      navigationData?.originUrl ||
      (this.currentUnitApplicationParentType === UnitApplicationParentType.LP
        ? this.routingService.lpBankAccounts
        : this.routingService.bankAccountsPage('applications'));
    this.currentUnitApplicationParentType =
      +this.route.snapshot.queryParams?.type ||
      history.state?.applicationType ||
      this.currentUnitApplicationParentType;
    this.preselectedHoldingId =
      navigationData?.holdingId || redirectedFromHoldingId;
    this.preselectedCompanyId =
      navigationData?.companyId || redirectedFromCompanyId;

    this.isCreateApplicationClickedBeforeRedirect =
      history.state?.continueCreate || false;
    this.titleService.setTitle(
      `Bank Account Application ${this.currentUnitApplicationParentType === UnitApplicationParentType.LP
        ? TerraUtils.consts.LpPageTitleSuffix
        : TerraUtils.consts.GpPageTitleSuffix
      }`
    );
    this.redirectIfNotDraft();

    this.selectableHoldings$ = combineLatest({
      holdings: this.allHoldings$,
      application: this.application$,
    }).pipe(
      map((data) =>
        data.holdings.filter(
          (h) =>
            (coerceBooleanProperty(!h.unitApplicationId) ||
              data.application?.holdingId === h.id) &&
            !h.isExample
        )
      ),
      shareReplay(1)
    );

    this.dataLoaded$ = forkJoin({ x: this.allHoldings$ }).pipe(
      take(1),
      shareReplay(1)
    );

    this.disabledHoldings$ = combineLatest({
      holdings: this.allHoldings$,
      application: this.application$,
    }).pipe(
      map((data) =>
        data.holdings.filter(
          (h) =>
            h.unitApplicationId &&
            (!data.application || h.id === data.application?.id)
        )
      )
    );

    this.preselectedHolding$ = combineLatest([
      this.preselectedHoldingId$,
      this.selectableHoldings$,
      this.disabledHoldings$,
    ]).pipe(
      map(([preselectedHoldingId, selectableHoldings, disabledHoldings]) => {
        if (!!preselectedHoldingId) {
          let _preSelectedHolding =
            selectableHoldings.find((h) => h.id === preselectedHoldingId) ||
            null;

          if (!_preSelectedHolding) {
            _preSelectedHolding =
              disabledHoldings.find((h) => h.id === preselectedHoldingId) ||
              null;
          }

          return _preSelectedHolding;
        }
      }),
      shareReplay(1)
    );

    this.preselectedCompany$ = combineLatest([this.preselectedCompanyId$, this.allCompanies$]).pipe(map(([preSelectedId, allCompanies]) => allCompanies.find(company => company.id === preSelectedId)), shareReplay(1));
    if (!this.preselectedCompanyId) {
      this.preselectedCompanyId$.pipe(take(1), tap(companyId => this.preselectedCompanyId = companyId)).subscribe();
    }
    this.initForm()
    .pipe(
        tap(() =>this.triggerContinueApplicationCreateIfNeeded())
    ).subscribe(() => {
      this.cdr.detectChanges();
      this.pageForm.markAsPristine();
      this.pageForm.markAsUntouched();
    });

    // Automatically saves the progress of form completion after "autoSavingTime" has elapsed since the last edit.
    const isSaving$ = new BehaviorSubject(false); // Just to make sure we don't send out two simultaneous calls.
    const takeTrailing = { leading: false, trailing: true };
    this.pageForm.valueChanges.pipe(
      skipUntil(this.pageForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(5000), take(1))), //Wait until the form stabilizes(almost 600 events are fired, crazy stuff)
      throttle(() => //we're throttling by time and then until the previous submission completes
            timer(this.autoSavingTime).pipe(
              throttle(() =>
                isSaving$.pipe(filter((status) => !status)
                ), takeTrailing),
        ), takeTrailing),
      tap(() => isSaving$.next(true)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      const model = this.generateSubmitModel(true);

      this.createOrUpdateApplication(model, 'draft').pipe(finalize(() => isSaving$.next(false)))
        .subscribe((response) => {
          this.snackbarService.showGeneralMessage(`${this.isGpSide ? 'CRE' : 'Unit'} application draft saved successfully`);
      });
    });
  }

  private navigateToOriginUrl() {
    this.router.navigateByUrl(this.originUrl);
  }

  goBack() {
    if (history.state?.navigationId > 1) {
      this.location.back();
    }
    else {
      this.navigateToOriginUrl();
    }
  }

  cancel() {
    if(this.pageForm.pristine || this.pageForm.untouched) {
      this.navigateToOriginUrl();
    } else {
      this.confirmCancel();
    }
  }

  confirmCancel() {
    const dialogParams = new ConfirmDialogParams();
    dialogParams.panelClass = 'delete-contact-dialog';
    dialogParams.actionLabel = `Discard Changes`;
    dialogParams.cancelLabel = `Continue Editing`;
    dialogParams.title = `Are You Sure?`;
    dialogParams.description = `Are you sure you want to discard your changes?`;

    this.dialogService.confirmDialog(dialogParams).afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.pageForm.reset();
        this.pageForm.markAsPristine();
        this.pageForm.markAsUntouched();
        this.cdr.detectChanges();
        this.navigateToOriginUrl();
      }
    });
  }

  sameAsContactChanged() {
    if (this.officerGroup.controls.sameAsContact.value) {
      this.officerGroup.patchValue(
        Object.entries(this.contactGroup.controls).reduce(
          (acc, [k, v]) => ({ ...acc, [k]: v.value }),
          {}
        )
      );
      Object.keys(this.contactGroup.controls).forEach((cName) =>
        this.officerGroup.controls[cName]?.disable()
      );
      return;
    }
    Object.keys(this.contactGroup.controls).forEach((cName) => {
      cName === 'phone'
        ? this.officerGroup.controls[cName]?.setValue('+1 ')
        : this.officerGroup.controls[cName]?.reset();
      this.officerGroup.controls[cName]?.updateValueAndValidity();
      this.officerGroup.controls[cName]?.enable();
    });
  }

  /** Creates or updates an application draft. Doesn't send it to Unit. */
  saveDraft() {
    if (this.pageForm.get('holdingId').hasError('notAllowed')) {
      this.pageForm.get('holdingId').markAsTouched();
      this.isSubmitAttempt$.next(true);
      return;
    }

    const model = this.generateSubmitModel(true);

    this.processingSubmit$.next(true);

    this.createOrUpdateApplication(model, 'draft').subscribe({
      next: (response) => {
        this.snackbarService.showGeneralMessage(
          `${this.isGpSide ? 'CRE' : 'Unit'
          } application draft saved successfully`
        );
        this.goBack();
        this.processingSubmit$.next(false);
      },
      error: (error) => {
        if (error instanceof BaseResponseDto) {
          this.utilsService.alertErrorMessage(error);
        }
        this.processingSubmit$.next(false);
      }
    });
  }

  /** Create an application in Unit (Sends the request to Unit, and does our additional logic) */
  startApplicationCreate() {
    this.pageForm.markAllAsTouched();
    this.pageForm.get('address').markAllAsTouched();
    this.isSubmitAttempt$.next(true);
    this.isGeneralServerError$.next(false);
    this.hasServerErrorMessage$.next(false);

    this.cleanUpSsnPassportInForm();
    if (!this.pageForm.valid) {
      return;
    }

    const model = this.generateSubmitModel();
    this.processingSubmit$.next(true);

    combineLatest([
      this.createOrUpdateApplication(model, 'full'),
      this.isEditMode$,
    ])
      .pipe(take(1))
      .subscribe(
        ([application, isEditMode]) => {
          // If the page is in Edit mode, continue to phone verification:
          // and if in Create mode, redirect to the Edit mode.
          if (isEditMode) {
            this.continueApplicationCreate(
              application.id,
              application.phoneVerification.isPhoneVerified
            );
          } else {
            UnitCreateEditApplicationComponent.fullAppData = model;
            this.navigateToEditAndTriggerCreateApplication(application);
          }
        },
        (error) => {
          if (error instanceof BaseResponseDto) {
            this.utilsService.alertErrorMessage(error);
          } else {
            this.isGeneralServerError$.next(true);
          }
          this.processingSubmit$.next(false);
        }
      );
  }

  /**
   * Add a beneficial owner form group to the beneficialOwners array.
   * @param beneficialOwner If a model is provided, the new form group will be populated.
   */
  addBeneficialOwner(beneficialOwner: UnitBeneficialOwnerReqRes = null): void {
    const beneficialOwnerForm = this.generateBeneficialOwnerFormGroup();
    if (beneficialOwner) {
      const formValues =
        this.generateBeneficialOwnerFormGroupValue(beneficialOwner);
      beneficialOwnerForm.patchValue(formValues);
    }
    this.beneficialOwnersFormArray.push(beneficialOwnerForm);
  }

  removeBeneficialOwnerAt(index: number) {
    const beneficialOwnerFormGroup = this.beneficialOwnersFormArray.controls[
      index
    ] as UntypedFormGroup;
    const addressCountrySubscriptionId = +beneficialOwnerFormGroup.get(
      this.beneficialOwnerAddressCountrySubscriptionIdProp
    ).value;

    const subscription = this.subscriptions.get(addressCountrySubscriptionId);
    subscription.unsubscribe();
    this.subscriptions.delete(addressCountrySubscriptionId);

    this.beneficialOwnersFormArray.removeAt(index);
  }

  openConfirmationModal(): Observable<boolean> {
    const dialogParams = new ConfirmDialogParams();
    dialogParams.title = `Incomplete Application – Save Your Progress?`;
    dialogParams.description = `You are exiting your bank account application without completing it. Would you like to save your progress as a draft, discard it, or continue editing?`;
    dialogParams.actionLabel = `Save as Draft`;
    dialogParams.secondaryActionLabel = `Delete Draft`;
    dialogParams.cancelLabel = `Continue Editing`;

    return this.dialogService.confirmDialog(dialogParams).afterClosed().pipe(
      switchMap((res) => {
        if (res?.secondaryAction) {
          return this.unitBankApplicationService.deleteDraft().pipe(
            map(() => true),
            catchError((error: Error) => {
              if (error.cause["code"] !== "NaNApplicationId") {
                this.snackbarService.showGeneralMessage(
                  `An error occurred while saving the draft.`
                );
              };
              return of(true);
            })
          );
        }
        else if (res) {
          const model = this.generateSubmitModel(true);

          this.processingSubmit$.next(true);

          return this.createOrUpdateApplication(model, 'draft').pipe(
            tap(() => {
              this.snackbarService.showGeneralMessage(
                `${this.isGpSide ? 'CRE' : 'Unit'
                } application draft saved successfully`
              );
              this.processingSubmit$.next(false);
            }),
            map(() => true),
            catchError((error) => {
              if (error instanceof BaseResponseDto) {
                this.utilsService.alertErrorMessage(error);
              }
              else {
                this.snackbarService.showGeneralMessage(
                  `An error occurred while saving the draft.`
                );
              }
              this.processingSubmit$.next(false);
              return of(false);
            })
          );
        }
        return of(false);
      })
    );
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (!this.pageForm.pristine && !this.isSaved) {
      const params = new ConfirmDialogParams(
        'Cancel CRE Bank application?',
        'Any information you added will be lost',
        'OK',
        'Stay on page'
      );
      return this.dialogService.confirmDialog(params).afterClosed();
    }
    return true;
  }

  onOfficerAddressFormReady(addressFormGroup: UntypedFormGroup) {
    const ssnOrPassportControl = addressFormGroup.parent.get(
      'officer.ssnOrPassport'
    );
    ssnOrPassportControl.updateValueAndValidity();
    addressFormGroup
      .get('countryId')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        ssnOrPassportControl.updateValueAndValidity();
      });
  }

  onBeneficialOwnerAddressFormReady(addressFormGroup: UntypedFormGroup) {
    const ssnOrPassportControl = addressFormGroup.parent.get('ssnOrPassport');
    ssnOrPassportControl.updateValueAndValidity();

    const subscription = addressFormGroup
      .get('countryId')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        ssnOrPassportControl.updateValueAndValidity();
      });

    const uniqueIdForDestroy = +addressFormGroup.parent.get(
      this.beneficialOwnerAddressCountrySubscriptionIdProp
    ).value;
    this.subscriptions.set(uniqueIdForDestroy, subscription);
  }

  holdingTrackByFn(index, item: GpHoldingListItemReqRes) {
    return item?.id;
  }

  getApplicationPageUrl(applicationId: number) {
    return this.routingService.bankApplicationPage(applicationId);
  }

  private redirectIfNotDraft() {
    this.application$.pipe(take(1)).subscribe((application) => {
      if (application && application.status !== UnitApplicationStatus.Draft) {
        this.router.navigateByUrl(
          this.routingService.bankApplicationPage(application.id)
        );
      }
    });
  }

  get isGpSide() {
    return (
      this.currentUnitApplicationParentType !== UnitApplicationParentType.LP
    );
  }

  /** Generate the form and populate it with data if in edit mode. */
  private initForm(): Observable<any> {
    this.generateForm();
    this.isGpSide ? this.initSelectedHoldingApplicationPageUrl() : '';
    return combineLatest([this.application$, this.isGpSide && this.preselectedCompanyId ? this.preselectedCompany$ : of([])]).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(([application, selectedCompany]) => {
        if (application) {
          if (application.companyDetailsId) {
            this.pageForm.get('name').disable();
            this.pageForm.get('entityType').disable();
          }
          this.populateForm(application);
        }
        if (!application) {
          this.autoPopulateBeneficialOwners();
          if (this.isGpSide && !!this.preselectedHoldingId) {
            this.pageForm.get('holdingId').setValue(this.preselectedHoldingId);
          }
          if (this.isGpSide && !!this.preselectedCompanyId) {
            this.pageForm.get('name').setValue(selectedCompany.name);
            this.pageForm.get('entityType').setValue(BusinessEntityType.toString(selectedCompany.entityType));
            this.pageForm.get('name').disable();
            this.pageForm.get('entityType').disable();
          }
        }
      })
    );
  }

  private getIsHoldingIdAllowed(holdingId: number) {
    return this.selectableHoldings$.pipe(
      // take(1),
      takeUntilDestroyed(this.destroyRef),
      map((selectableHoldings) => {
        return selectableHoldings.map((h) => h.id).indexOf(holdingId) !== -1;
      })
    );
  }

  private initSelectedHoldingApplicationPageUrl() {
    this.selectedHoldingApplicationPageUrl$ = combineLatest([
      this.pageForm.get('holdingId').valueChanges,
      this.disabledHoldings$,
    ]).pipe(
      takeUntilDestroyed(this.destroyRef),
      map(([holdingId, disabledHoldings]) => {
        const applicationId = disabledHoldings.find(
          (h) => h.id === holdingId
        )?.unitApplicationId;
        return this.routingService.bankApplicationPage(applicationId);
      }),
      shareReplay(1)
    );
  }

  validateSelectedHolding(
    control: AbstractControl,
    originalValue = ''
  ): ValidationErrors {
    const { value }: { value: string } = control;

    if (!control.valueChanges || control.pristine || !value || value === originalValue) {
      return of(null);
    }

    const holdingId = +value;
    return this.getIsHoldingIdAllowed(holdingId).pipe(
      map((isAllowed) => {
        return isAllowed ? null : { notAllowed: true };
      })
    );
  }

  /** When in new application page (create state, not edit), there is no draft application yet.
   * If a user clicks the Create button without first saving a draft, we are saving a draft first for him
   * (in order to be able to verify the business phone prior to actually sending the application to Unit).
   * After saving the draft, we redirect to the draft edit page, and should continue with creating the application.
   */
  private triggerContinueApplicationCreateIfNeeded() {
    const isContinue = this.isCreateApplicationClickedBeforeRedirect;
    if (isContinue) {
      this.application$.pipe(take(1)).subscribe((application) => {
        this.continueApplicationCreate(
          application.id,
          application.phoneVerification.isPhoneVerified
        );
      });
    }
  }

  private createOrUpdateApplication(
    model: UnitBusinessApplicationReqRes,
    serverValidationLevel: UnitApplicationValidationLevel
  ) {
    return this.currentApplicationId$.pipe(
      take(1),
      switchMap((applicationId) => {
        if (applicationId) {
          return this.bankApplicationDataService.updateDraft(
            applicationId,
            model,
            serverValidationLevel
          ).pipe(
            tap((response) => {
              this.pageForm.markAsPristine();
            })
          );
        }
        return this.bankApplicationDataService.createDraft(
          model,
          serverValidationLevel
        ).pipe(
          tap((response) => {
            this.pageForm.markAsPristine();
            this.currentApplicationId$.next(response.id);
          })
        );
      })
    );
  }

  /** Continues the process of creating the application,
   * from a stage where the applilaction is a already saved as a draft.
   */
  private continueApplicationCreate(
    applicationId: number,
    isPhoneVerified: boolean
  ) {
    this.processingSubmit$.next(true);
    this.isGeneralServerError$.next(false);
    this.hasServerErrorMessage$.next(false);
    this.serverErrorMessage = null;
    const isPhoneVerified$ = isPhoneVerified
      ? of(true)
      : this.verifyPhoneProcess(applicationId);
    let model = this.generateSubmitModel();

    if (UnitCreateEditApplicationComponent.fullAppData) {
      model = this.mergeFullApplicationData(model);
    }

    isPhoneVerified$
      .pipe(
        tap((isVerified) => {
          if (!isVerified) {
            this.processingSubmit$.next(false);
          }
        }),
        filter((isVerified) => isVerified),
        switchMap(() =>
          this.bankApplicationDataService.sendToUnit(applicationId, model)
        )
      )
      .subscribe({
        next: (application) => {
          if (!application || !application.id) {
            this.isGeneralServerError$.next(true);
            this.loggerService
              .error(`Error while trying to create an application.
            ${!application
                  ? ' Response contains no application.'
                  : ' Response contains no applicationId'
                }`);
          } else {
            this.pageForm.markAsPristine();
            switch (application.parentType) {
              case UnitApplicationParentType.Holding:
                this.snackbarService.showGeneralMessage(
                  'CRE application created successfully'
                );
                this.router.navigateByUrl(
                  this.routingService.bankApplicationPage(application.id)
                );
                break;
              case UnitApplicationParentType.Organization:
                this.snackbarService.showGeneralMessage(
                  'CRE application created successfully'
                );
                this.router.navigateByUrl(
                  this.routingService.bankApplicationPage(application.id)
                );
                break;
              case UnitApplicationParentType.LP:
                this.snackbarService.showGeneralMessage(
                  'Investor application created successfully'
                );
                this.router.navigateByUrl(
                  this.routingService.lpUnitApplication(application.id)
                );
                break;
              default:
                this.router.navigateByUrl(TerraUtils.consts.paths.NOT_FOUND);
                break;
            }
          }
          this.processingSubmit$.next(false);
        },
        error: (error) => {
          this.processingSubmit$.next(false);
          if (error?.error?.innerException?.exceptionMessage) {
            this.serverErrorMessage =
              'Please fix the following issues:\n\t' +
              JSON.parse(error.error.innerException.exceptionMessage).join(
                '\n\t'
              );
            this.hasServerErrorMessage$.next(true);
          } else {
            this.isGeneralServerError$.next(true);
          }
        }
      });
  }

  private verifyPhoneProcess(applicationId: number): Observable<boolean> {
    const phoneToVerify = this.pageForm.get('contact.phone').value;
    const phoneNumberObject = UnitPhone.fromFormValue(phoneToVerify);
    const dialogConfig = new MatDialogConfig<PhoneVerificationDialogContext>();
    dialogConfig.data = new PhoneVerificationDialogContext();
    dialogConfig.data.phone = `${phoneNumberObject.countryCode}${phoneNumberObject.number}`;
    dialogConfig.data.isUserAbleToEditPhoneNumber = true;
    dialogConfig.viewContainerRef = this.vcr;
    return this.dialog
      .open(PhoneVerificationDialogComponent, dialogConfig)
      .afterClosed()
      .pipe();
  }

  private generateAddressFormSettings(): AddressFormSettings {
    // tslint:disable-next-line:no-bitwise
    const fieldsToShow =
      AddressFields.Country |
      AddressFields.Street |
      AddressFields.Street2 |
      AddressFields.City |
      AddressFields.State |
      AddressFields.PostalCode;
    return new AddressFormSettings(fieldsToShow, undefined, true);
  }

  private generateForm() {
    this.pageForm = this.fb.group({
      holdingId: null,
      name: [null, [Validators.required, Validators.maxLength(255)]],
      dba: [null, [Validators.required, Validators.maxLength(255)]],
      bankAccountName: [null, [Validators.required, Validators.maxLength(50)]],
      address: {},
      stateOfIncorporation: [null, Validators.required],
      yearOfIncorporation: [null, Validators.required],
      businessVertical: [null, Validators.required],
      ein: [null, [Validators.required, Validators.pattern(`^\\d{9}$`)]],
      entityType: [null, Validators.required],
      businessContact: this.fb.group({
        phone: [
          '+1 ',
          [
            Validators.required,
            CustomValidators.InternationalPhoneNumber.bind(this),
          ],
        ],
        website: [
          null,
          [Validators.required, Validators.pattern(this.unitWebsitePattern)],
        ],
        notHaveWebsite: false,
      }),
      contact: this.fb.group({
        firstName: [
          null,
          [
            Validators.required,
            Validators.maxLength(this.contactFirstNameMaxLength),
          ],
        ],
        lastName: [
          null,
          [
            Validators.required,
            Validators.maxLength(this.contactLastNameMaxLength),
          ],
        ],
        phone: [
          '+1 ',
          [
            Validators.required,
            CustomValidators.InternationalPhoneNumber.bind(this),
          ],
        ],
        email: [null, [Validators.required, Validators.email]],
      }),
      officer: this.fb.group({
        sameAsContact: true,
        firstName: [
          null,
          [
            Validators.required,
            Validators.minLength(1),
            Validators.maxLength(this.contactFirstNameMaxLength),
          ],
        ],
        lastName: [
          null,
          [
            Validators.required,
            Validators.maxLength(this.contactLastNameMaxLength),
          ],
        ],
        title: [null, Validators.required],
        dateOfBirth: [null, Validators.required],
        // One of SSN or Password is requires:
        ssnOrPassport: [
          SsnOrPassport.SSN,
          [
            CustomValidators.patternIf(
              () => this.mustFillOfficerPassport(),
              `^[${SsnOrPassport.Passport}]$`
            ),
          ],
        ],
        ssn: [
          null,
          [
            CustomValidators.requiredIf(
              () =>
                this.pageForm.get('officer.ssnOrPassport').value ===
                SsnOrPassport.SSN
            ),
            Validators.minLength(1),
            Validators.maxLength(30),
            Validators.pattern(`^\\d{9}$`),
          ],
        ],
        passport: [
          null,
          [
            CustomValidators.requiredIf(
              () =>
                this.pageForm.get('officer.ssnOrPassport').value ===
                SsnOrPassport.Passport
            ),
            Validators.minLength(3),
            Validators.maxLength(30),
          ],
        ],
        nationality: [null, Validators.required],
        phone: [
          '+1 ',
          [
            Validators.required,
            CustomValidators.InternationalPhoneNumber.bind(this),
          ],
        ],
        email: [null, [Validators.required, Validators.email]],
        occupation: null,
        annualIncome: null,
        sourceOfIncome: null,
      }),
      officerAddress: {},
      beneficialOwners: this.fb.array([]),
      approveInformation: [false, Validators.requiredTrue],
      termsOfServiceAgreement: [false, Validators.requiredTrue],
      annualRevenue: null,
      numberOfEmployees: null,
      countriesOfOperation: null,
      cashFlow: null,
    });

    if (
      this.currentUnitApplicationParentType ===
      UnitApplicationParentType.Holding
    ) {
      this.pageForm.get('holdingId').setValidators(Validators.required);
      this.pageForm
        .get('holdingId')
        .setAsyncValidators(this.validateSelectedHolding.bind(this));
      this.pageForm
        .get('holdingId')
        .updateValueAndValidity({ emitEvent: true });
    }

    this.setMoreDetailsNationality(this.pageForm.get('officer'));

    this.additionalBusinessInformationValidation();
    if (this.officerGroup.controls.sameAsContact.value) {
      this.enableSetOfficerFieldsAsContactFields();
    }
    this.registerNationalityChange(
      this.pageForm.get('officer.nationality'),
      this.pageForm.get('officer.ssnOrPassport')
    );
    this.refreshValidityWhenControlValueChanged(
      this.pageForm.get('officer.ssn'),
      this.pageForm.get('officer.ssnOrPassport')
    );
    this.refreshValidityWhenControlValueChanged(
      this.pageForm.get('officer.passport'),
      this.pageForm.get('officer.ssnOrPassport')
    );
    this.refreshValidityWhenControlValueChanged(
      this.pageForm.get('officer.nationality'),
      this.pageForm.get('officer.ssnOrPassport')
    );
  }

  get businessContactGroup() {
    return this.pageForm.get('businessContact');
  }

  get officerGroup(): UntypedFormGroup {
    return this.pageForm.get('officer') as UntypedFormGroup;
  }

  get contactGroup(): UntypedFormGroup {
    return this.pageForm.get('contact') as UntypedFormGroup;
  }

  private contactListeningEnabled(value: any, index: number): boolean {
    return value;
  }

  private enableSetOfficerFieldsAsContactFields() {
    this.refreshValueWhenControlValueChanged(
      this.officerGroup.controls.firstName,
      this.contactGroup.controls.firstName,
      concat(
        of(this.officerGroup.controls.sameAsContact.value),
        this.officerGroup.controls.sameAsContact.valueChanges
      ),
      this.contactListeningEnabled
    );
    this.refreshValueWhenControlValueChanged(
      this.officerGroup.controls.lastName,
      this.contactGroup.controls.lastName,
      concat(
        of(this.officerGroup.controls.sameAsContact.value),
        this.officerGroup.controls.sameAsContact.valueChanges
      ),
      this.contactListeningEnabled
    );
    this.refreshValueWhenControlValueChanged(
      this.officerGroup.controls.phone,
      this.contactGroup.controls.phone,
      concat(
        of(this.officerGroup.controls.sameAsContact.value),
        this.officerGroup.controls.sameAsContact.valueChanges
      ),
      this.contactListeningEnabled
    );
    this.refreshValueWhenControlValueChanged(
      this.officerGroup.controls.email,
      this.contactGroup.controls.email,
      concat(
        of(this.officerGroup.controls.sameAsContact.value),
        this.officerGroup.controls.sameAsContact.valueChanges
      ),
      this.contactListeningEnabled
    );

    this.officerGroup.controls.firstName?.disable();
    this.officerGroup.controls.lastName?.disable();
    this.officerGroup.controls.phone?.disable();
    this.officerGroup.controls.email?.disable();
  }

  private generateBeneficialOwnerFormGroup(): UntypedFormGroup {
    const formGroup = this.fb.group({
      id: null,
      firstName: [
        null,
        [
          Validators.required,
          Validators.maxLength(this.contactFirstNameMaxLength),
        ],
      ],
      lastName: [
        null,
        [
          Validators.required,
          Validators.maxLength(this.contactLastNameMaxLength),
        ],
      ],
      dateOfBirth: [null, [Validators.required]],
      // One of SSN or Password is requires:
      ssnOrPassport: SsnOrPassport.SSN,
      ssn: null, // validators are being set later
      passport: null, // validators are being set later
      nationality: [null, Validators.required],
      percentage: [
        null,
        [Validators.required, Validators.min(25), Validators.max(100)],
      ],
      phone: [
        '+1 ',
        [
          Validators.required,
          CustomValidators.InternationalPhoneNumber.bind(this),
        ],
      ],
      email: [null, [Validators.required, Validators.email]],
      address: {},
      occupation: null,
      annualIncome: null,
      sourceOfIncome: null,
    });

    formGroup
      .get('percentage')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((value) => {
        formGroup
          .get('percentage')
          .setValue(this.numberPipe.transform(value, 0));
        formGroup.get('percentage').updateValueAndValidity();
      });

    this.registerNationalityChange(
      formGroup.get('nationality'),
      formGroup.get('ssnOrPassport')
    );

    // Set a control with a unique incrementing ID that will be used
    // to find the subscription that should be unsubscribed for the address country changes:
    formGroup.setControl(
      this.beneficialOwnerAddressCountrySubscriptionIdProp,
      this.fb.control(this.newSubscriptionId)
    );

    const ssnOrPassport = formGroup.controls.ssnOrPassport;
    const ssn = formGroup.controls.ssn;
    const passport = formGroup.controls.passport;
    const nationality = formGroup.controls.nationality;

    // If the beneficial owner address is not in the United States, user must fill passport (not SSN).
    ssnOrPassport.setValidators([
      CustomValidators.patternIf(
        () => this.mustFillBeneficialOwnerPassport(formGroup),
        `^[${SsnOrPassport.Passport}]$`
      ),
    ]);

    ssn.setValidators([
      CustomValidators.requiredIf(() => {
        return ssnOrPassport.value === SsnOrPassport.SSN;
      }),
      Validators.minLength(1),
      Validators.maxLength(30),
      Validators.pattern(`^\\d{9}$`),
    ]);

    passport.setValidators([
      CustomValidators.requiredIf(
        () => ssnOrPassport.value === SsnOrPassport.Passport
      ),
      Validators.minLength(3),
      Validators.maxLength(30),
    ]);

    // nationality.setValidators([CustomValidators.requiredIf(() => ssnOrPassport.value === SsnOrPassport.Passport)]);
    nationality.setValidators([Validators.required]);

    this.refreshValidityWhenControlValueChanged(ssn, ssnOrPassport);
    this.refreshValidityWhenControlValueChanged(passport, ssnOrPassport);
    this.refreshValidityWhenControlValueChanged(nationality, ssnOrPassport);

    this.setMoreDetailsNationality(formGroup);

    return formGroup;
  }

  private setMoreDetailsNationality(groupToWatch: AbstractControl) {
    groupToWatch
      .get('nationality')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((nationality) => {
        if (
          nationality === this.UnitedStatesId &&
          this.officerGroup.get('nationality').value === this.UnitedStatesId &&
          this.beneficialOwnersFormArray.controls.every(
            (control) =>
              control.get('nationality').value === this.UnitedStatesId
          )
        ) {
          this.clearRequiredValidator(this.officerGroup.get('occupation'));
          this.clearRequiredValidator(this.officerGroup.get('annualIncome'));
          this.clearRequiredValidator(this.officerGroup.get('sourceOfIncome'));

          this.beneficialOwnersFormArray.controls.forEach((control) => {
            this.clearRequiredValidator(control.get('occupation'));
            this.clearRequiredValidator(control.get('annualIncome'));
            this.clearRequiredValidator(control.get('sourceOfIncome'));
          });
        } else {
          this.setRequiredValidator(this.officerGroup.get('occupation'));
          this.setRequiredValidator(this.officerGroup.get('annualIncome'));
          this.setRequiredValidator(this.officerGroup.get('sourceOfIncome'));

          this.beneficialOwnersFormArray.controls.forEach((control) => {
            this.setRequiredValidator(control.get('occupation'));
            this.setRequiredValidator(control.get('annualIncome'));
            this.setRequiredValidator(control.get('sourceOfIncome'));
          });
        }
      });
  }

  private setRequiredValidator(control: AbstractControl) {
    control.setValidators(Validators.required);
    control.updateValueAndValidity();
  }

  private clearRequiredValidator(control: AbstractControl) {
    control.clearValidators();
    control.updateValueAndValidity();
  }

  private registerNationalityChange(
    controlToWatch: AbstractControl,
    controlToChange: AbstractControl
  ) {
    controlToWatch.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe((value) => {
        controlToChange.updateValueAndValidity();
        controlToChange.setValue(
          !value || value === this.UnitedStatesId
            ? SsnOrPassport.SSN
            : SsnOrPassport.Passport
        );
      });
  }

  /** The user must fill officer passport (not SSN) if the nationality is not the United States. */
  public mustFillOfficerPassport() {
    /** The user must fill officer passport (not SSN) if the address country is outside the United States. */
    // const countryId = this.pageForm.get('officerAddress.countryId')?.value;
    const countryId = this.pageForm.get('officer.nationality')?.value;
    return countryId && countryId !== this.UnitedStatesId;
  }

  /** The user must fill the beneficial owner passport (not SSN) if the nationality is not the United States. */
  public mustFillBeneficialOwnerPassport(
    beneficialOwnerFormGroup: UntypedFormGroup
  ): boolean {
    /** The user must fill the beneficial owner passport (not SSN) if the address country is outside the United States. */
    // const countryId = beneficialOwnerFormGroup.get('address.countryId')?.value;
    const countryId = beneficialOwnerFormGroup.get('nationality')?.value;
    return countryId && countryId !== this.UnitedStatesId;
  }

  public isUnitedStateNationality(formGroup: UntypedFormGroup): boolean {
    return (
      this.officerGroup.get('nationality').value === this.UnitedStatesId &&
      this.beneficialOwnersFormArray.controls.every(
        (control) => control.get('nationality').value === this.UnitedStatesId
      )
    );
  }

  webSiteCheckbox(checked) {
    if (checked) {
      this.businessContactGroup.get('website').clearValidators();
      this.businessContactGroup.get('website').disable();
    } else {
      this.businessContactGroup.get('website').enable();
      this.businessContactGroup
        .get('website')
        .addValidators([
          Validators.required,
          Validators.pattern(this.unitWebsitePattern),
        ]);
    }
    this.businessContactGroup.get('website').updateValueAndValidity();
  }

  private populateForm(model: UnitBusinessApplicationReqRes) {
    this.pageForm.patchValue(
      {
        holdingId: model.holdingId,
        name: model.name,
        dba: model.dba,
        bankAccountName: model.bankAccountName,
        address: this.getFormValueFromUnitAddress(model.address),
        stateOfIncorporation: model.stateOfIncorporation
          ? this.allStates.find(
            (s) => s.abbreviation === model.stateOfIncorporation
          ).name
          : null,
        yearOfIncorporation: !!model.yearOfIncorporation
          ? moment().startOf('year').year(model.yearOfIncorporation)
          : null,
        businessVertical: model.businessVertical,
        ein: model.ein,
        entityType: model.entityType,
        businessContact: {
          phone: UnitPhone.toFormValue(model.phone),
          website: model.website,
          notHaveWebsite: model.notHaveWebsite,
        },
        contact: {
          firstName: model.contact.fullName.first,
          lastName: model.contact.fullName.last,
          phone: UnitPhone.toFormValue(model.contact.phone),
          email: model.contact.email,
        },
        officer: {
          sameAsContact:
            model.officer.fullName.first == model.contact.fullName.first &&
            model.officer.fullName.last == model.contact.fullName.last &&
            model.officer.phone == model.contact.phone &&
            model.officer.email == model.contact.email,
          firstName: model.officer.fullName.first,
          lastName: model.officer.fullName.last,
          title: model.officer.title,
          dateOfBirth: model.officer.dateOfBirth,
          ssnOrPassport:
            model.officer.ssn || !model.officer.passport
              ? SsnOrPassport.SSN
              : SsnOrPassport.Passport,
          ssn: model.officer.ssn,
          passport: model.officer.passport,
          nationality: this.getCountryByCode(model.officer.nationality)?.id,
          phone: UnitPhone.toFormValue(model.officer.phone),
          email: model.officer.email,
          occupation: model.officer.occupation,
          annualIncome: model.officer.annualIncome,
          sourceOfIncome: model.officer.sourceOfIncome,
        },
        officerAddress: this.getFormValueFromUnitAddress(model.officer.address),
        beneficialOwners: [],
        annualRevenue: model.annualRevenue,
        numberOfEmployees: model.numberOfEmployees,
        countriesOfOperation: model.countriesOfOperation,
        cashFlow: model.cashFlow,
      },
      { emitEvent: true }
    );
    this.currentUnitApplicationParentType = model.parentType;
    if (this.currentUnitApplicationParentType === UnitApplicationParentType.Organization) {
      this.pageForm.get('holdingId').clearValidators();
      this.pageForm
        .get('holdingId')
        .clearAsyncValidators();
      this.pageForm
        .get('holdingId')
        .updateValueAndValidity({ emitEvent: true });
    }

    model.beneficialOwners.forEach((owner) => {
      this.addBeneficialOwner(owner);
    });

    if (!!this.pageForm.get('businessContact.notHaveWebsite').value) {
      this.pageForm.get('businessContact.website').clearValidators();
      this.businessContactGroup.get('website').updateValueAndValidity();
    }
    if (!this.officerGroup.controls.sameAsContact.value) {
      this.officerGroup.controls.firstName?.enable();
      this.officerGroup.controls.lastName?.enable();
      this.officerGroup.controls.phone?.enable();
      this.officerGroup.controls.email?.enable();
    }
  }

  private additionalBusinessInformationValidation() {
    combineLatest([
      this.pageForm.get('officer').valueChanges,
      this.pageForm.get('beneficialOwners').valueChanges.pipe(startWith([])),
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(([officer, beneficialOwners]) => {
          return (
            officer.nationality !== this.UnitedStatesId ||
            beneficialOwners.some(
              (bo) => bo.nationality !== this.UnitedStatesId
            )
          );
        }),
        tap((nationality) =>
          this.showAdditionalBusinessInformation$.next(nationality)
        )
      )
      .subscribe((nationality) => {
        if (nationality) {
          this.setRequiredValidator(this.pageForm.get('annualRevenue'));
          this.setRequiredValidator(this.pageForm.get('numberOfEmployees'));
          this.setRequiredValidator(this.pageForm.get('countriesOfOperation'));
          this.setRequiredValidator(this.pageForm.get('cashFlow'));
        } else {
          this.clearRequiredValidator(this.pageForm.get('annualRevenue'));
          this.clearRequiredValidator(this.pageForm.get('numberOfEmployees'));
          this.clearRequiredValidator(
            this.pageForm.get('countriesOfOperation')
          );
          this.clearRequiredValidator(this.pageForm.get('cashFlow'));
        }
      });
  }

  private getCountryByCode(code: string): CountryModel {
    return code ? this.resourceService.getCountryByCode(code) : null;
  }

  private getCountryByName(name: string): CountryModel {
    return name ? this.resourceService.getCountryByName(name) : null;
  }

  private getCountryById(countryId: number): CountryModel {
    return !!countryId ? this.resourceService.getCountryById(countryId) : null;
  }

  public einChanged(): void {
    this.pageForm
      .get('ein')
      .setValue(this.pageForm.get('ein').value.replace(/\D/g, ''));
  }

  private generateSubmitModel(
    includeInvalid = false
  ): UnitBusinessApplicationReqRes {
    const model = new UnitBusinessApplicationReqRes();
    const formValues = includeInvalid
      ? this.pageForm.getRawValue()
      : this.pageForm.value;

    model.parentType = this.currentUnitApplicationParentType;
    model.holdingId = formValues.holdingId;
    model.companyDetailsId = this.preselectedCompanyId;
    model.name = this.preselectedCompanyId ? this.pageForm.get('name').getRawValue() : formValues.name;
    model.dba = formValues.dba;
    model.bankAccountName = formValues.bankAccountName;
    model.ein = formValues.ein;
    model.entityType = this.preselectedCompanyId ? this.pageForm.get('entityType').getRawValue() : formValues.entityType;
    model.stateOfIncorporation = formValues.stateOfIncorporation
      ? this.allStates.find((s) => s.name === formValues.stateOfIncorporation)
        .abbreviation
      : null;
    model.yearOfIncorporation = formValues.yearOfIncorporation?.year();
    model.businessVertical = formValues.businessVertical;
    model.address = this.populateUnitAddress(formValues.address);
    model.address.country = this.UnitedStatesCountry.code; // Business address country is locked to USA and not found in formValues because it's a disabled inpust
    model.phone = UnitPhone.fromFormValue(formValues.businessContact.phone);
    model.website = formValues.businessContact.notHaveWebsite
      ? null
      : this.urlUtilsService.removeProtocolFromUrl(
        formValues.businessContact.website
      );
    model.notHaveWebsite = formValues.businessContact.notHaveWebsite;

    if (this.showAdditionalBusinessInformation$.value) {
      model.annualRevenue = formValues.annualRevenue;
      model.numberOfEmployees = formValues.numberOfEmployees;
      model.countriesOfOperation = formValues.countriesOfOperation;
      model.cashFlow = formValues.cashFlow;
    } else {
      model.annualRevenue = null;
      model.numberOfEmployees = null;
      model.countriesOfOperation = null;
      model.cashFlow = null;
    }

    // Business contact
    model.contact = new UnitBusinessContact();
    model.contact.fullName = new UnitFullName(
      formValues.contact.firstName,
      formValues.contact.lastName
    );
    model.contact.email = formValues.contact.email;
    model.contact.phone = UnitPhone.fromFormValue(formValues.contact.phone);

    // Officer
    const officerForm = formValues.officer;
    model.officer = new UnitOfficer();
    model.officer.fullName = officerForm.sameAsContact
      ? new UnitFullName(
        formValues.contact.firstName,
        formValues.contact.lastName
      )
      : new UnitFullName(officerForm.firstName, officerForm.lastName);
    model.officer.title = officerForm.title;
    model.officer.dateOfBirth = officerForm.dateOfBirth;
    model.officer.nationality = officerForm.nationality
      ? this.allCountries.find((c) => c.id === officerForm.nationality).code
      : null;

    const nonUsaField =
      !formValues.beneficialOwners.every(
        (beneficialOwner) => beneficialOwner.nationality === this.UnitedStatesId
      ) || officerForm.nationality !== this.UnitedStatesId;

    if (nonUsaField) {
      model.officer.occupation = officerForm.occupation;
      model.officer.annualIncome = officerForm.annualIncome;
      model.officer.sourceOfIncome = officerForm.sourceOfIncome;
    }

    if (officerForm.ssnOrPassport === SsnOrPassport.SSN) {
      model.officer.ssn = officerForm.ssn;
    } else if (officerForm.ssnOrPassport === SsnOrPassport.Passport) {
      model.officer.passport = officerForm.passport;
    }
    model.officer.email = officerForm.sameAsContact
      ? formValues.contact.email
      : officerForm.email;
    model.officer.phone = officerForm.sameAsContact
      ? UnitPhone.fromFormValue(formValues.contact.phone)
      : UnitPhone.fromFormValue(officerForm.phone);
    model.officer.address = this.populateUnitAddress(formValues.officerAddress);

    model.beneficialOwners = new Array<UnitBeneficialOwnerReqRes>();

    formValues.beneficialOwners.forEach((beneficialOwner) => {
      model.beneficialOwners.push(
        this.generateBeneficialOwnerSubmitModel(beneficialOwner, nonUsaField)
      );
    });

    return model;
  }

  /** Generates a beneficial owner object populated with form values from a provided form group */
  private generateBeneficialOwnerSubmitModel(
    beneficialOwnerForm: any,
    nonUsaField: boolean
  ): UnitBeneficialOwnerReqRes {
    if (beneficialOwnerForm) {
      const beneficialOwner = {
        id: beneficialOwnerForm.id,
        fullName: {
          first: beneficialOwnerForm.firstName,
          last: beneficialOwnerForm.lastName,
        },
        dateOfBirth: beneficialOwnerForm.dateOfBirth,
        email: beneficialOwnerForm.email,
        phone: UnitPhone.fromFormValue(beneficialOwnerForm.phone),
        address: this.populateUnitAddress(beneficialOwnerForm.address),
        percentage: beneficialOwnerForm.percentage,
        nationality: beneficialOwnerForm.nationality
          ? this.allCountries.find(
            (c) => c.id === beneficialOwnerForm.nationality
          ).code
          : null,
      } as UnitBeneficialOwnerReqRes;

      if (beneficialOwnerForm.ssnOrPassport === SsnOrPassport.SSN) {
        beneficialOwner.ssn = beneficialOwnerForm.ssn;
      } else if (beneficialOwnerForm.ssnOrPassport === SsnOrPassport.Passport) {
        beneficialOwner.passport = beneficialOwnerForm.passport;
      }

      if (nonUsaField) {
        beneficialOwner.occupation = beneficialOwnerForm.occupation;
        beneficialOwner.annualIncome = beneficialOwnerForm.annualIncome;
        beneficialOwner.sourceOfIncome = beneficialOwnerForm.sourceOfIncome;
      }

      return beneficialOwner;
    }
    return {} as UnitBeneficialOwnerReqRes;
  }

  /** Generates a UnitForm object populated with an address form values */
  private populateUnitAddress(addressForm: any): UnitAddress {
    const address = new UnitAddress();
    if (addressForm) {
      address.street = addressForm.streetName;
      address.street2 = addressForm.street2Name;
      address.city = addressForm.cityName;
      address.state = addressForm.stateName
        ? this.allStates.find((s) => s.name === addressForm.stateName)
          ?.abbreviation
        : null;
      address.postalCode = addressForm.postalCode;
      address.country = addressForm.countryId
        ? this.allCountries.find((c) => c.id === addressForm.countryId)?.code
        : null;
    }
    return address;
  }

  /** Generate a model that fits the AddressFormComponent form fields  */
  private getFormValueFromUnitAddress(
    unitAddress: UnitAddress
  ): LocationDetailsResponse {
    if (unitAddress) {
      return {
        streetName: unitAddress.street,
        street2Name: unitAddress.street2,
        cityName: unitAddress.city,
        stateName: unitAddress.state
          ? this.allStates.find(
            (s) =>
              s.abbreviation === unitAddress.state ||
              s.name === unitAddress.state
          )?.name
          : null,
        postalCode: unitAddress.postalCode,
        countryId: unitAddress.country
          ? this.allCountries.find(
            (c) =>
              c.code === unitAddress.country || c.name === unitAddress.country
          )?.id
          : null,
      } as LocationDetailsResponse;
    }
    return {
      countryId: 1,
    } as LocationDetailsResponse;
  }

  /** Generates a beneficial owner object populated with form values from a provided form group */
  private generateBeneficialOwnerFormGroupValue(
    beneficialOwner: UnitBeneficialOwnerReqRes
  ): any {
    if (beneficialOwner) {
      let ssnOrPassport = SsnOrPassport.SSN;
      let nationality = null;
      if (beneficialOwner.passport) {
        ssnOrPassport = SsnOrPassport.Passport;
      } else if (
        beneficialOwner.nationality !== TerraUtils.consts.countryCode.US
      ) {
        ssnOrPassport = SsnOrPassport.Passport;
      }

      if (beneficialOwner.nationality) {
        nationality = this.getCountryByCode(beneficialOwner.nationality);
      } else if (!!beneficialOwner.address?.country) {
        nationality = this.getCountryByName(beneficialOwner.address.country);
      }

      const formValue = {
        id: beneficialOwner.id,
        firstName: beneficialOwner.fullName.first,
        lastName: beneficialOwner.fullName.last,
        dateOfBirth: beneficialOwner.dateOfBirth,
        ssnOrPassport,
        ssn: beneficialOwner.ssn,
        passport: beneficialOwner.passport,
        nationality: nationality?.id,
        email: beneficialOwner.email,
        phone: UnitPhone.toFormValue(beneficialOwner.phone),
        address: this.getFormValueFromUnitAddress(beneficialOwner.address),
        percentage: this.numberPipe.transform(beneficialOwner.percentage, 0),
        occupation: beneficialOwner.occupation,
        annualIncome: beneficialOwner.annualIncome,
        sourceOfIncome: beneficialOwner.sourceOfIncome,
      };
      return formValue;
    }
    return {};
  }

  private navigateToEditAndTriggerCreateApplication(
    application: UnitBusinessApplicationReqRes
  ) {
    switch (application.parentType) {
      case UnitApplicationParentType.Holding:
        this.router.navigateByUrl(
          this.routingService.bankApplicationDraftEditPage(application.id),
          { state: { continueCreate: true } }
        );
        break;
      case UnitApplicationParentType.Organization:
        this.router.navigateByUrl(
          this.routingService.bankApplicationDraftEditPage(application.id),
          {
            state: {
              continueCreate: true,
              applicationType: this.currentUnitApplicationParentType,
            },
          }
        );
        break;
      case UnitApplicationParentType.LP:
        this.router.navigateByUrl(
          this.routingService.lpEditUnitApplication(application.id),
          { state: { continueCreate: true } }
        );
        break;
      default:
        this.router.navigateByUrl(TerraUtils.consts.paths.NOT_FOUND);
        break;
    }
  }

  private refreshValidityWhenControlValueChanged(
    controlToRefresh: AbstractControl,
    controlToDependOn: AbstractControl
  ) {
    if (!controlToRefresh || !controlToDependOn) {
      return;
    }
    controlToDependOn.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        controlToRefresh.updateValueAndValidity();
      });
  }

  private refreshValueWhenControlValueChanged(
    controlToRefresh: AbstractControl,
    controlToDependOn: AbstractControl,
    dependsValueObservable: Observable<any>,
    registerCondition: (value: any, index: number) => boolean
  ) {
    if (!controlToRefresh || !controlToDependOn) {
      return;
    }
    controlToDependOn.valueChanges
      .pipe(
        withLatestFrom(dependsValueObservable, (data, dataDepends) => ({
          data,
          dataDepends,
        })),
        filter((value, index) => registerCondition(value.dataDepends, index)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((value) => {
        controlToRefresh.setValue(value.data);
        controlToRefresh.updateValueAndValidity();
     });
  }

  /**
   * Adds beneficial owners to the form from InvestingEntityOwnershipReqRes array
   * @param investorsOwnership investors ownership array
   */
  private addBeneficialOwnerFromInvestorOwnership(
    investorsOwnership: InvestingEntityOwnershipReqRes[]
  ) {
    const beneficialOwners = investorsOwnership.map((investor) => {
      const nameParts = investor.investingEntity.name.split(' ');
      const beneficialOwner = new UnitBeneficialOwnerReqRes();
      beneficialOwner.fullName = new UnitFullName();
      beneficialOwner.address = new UnitAddress();
      beneficialOwner.fullName.first =
        nameParts.length > 0 ? nameParts[0] : null;
      beneficialOwner.fullName.last =
        nameParts.length > 1
          ? nameParts.filter((val, i) => i !== 0).join(' ')
          : null;
      beneficialOwner.email = investor.contact?.email;
      beneficialOwner.percentage = investor.ownershipPercentage.toString();
      beneficialOwner.address.country = this.getCountryById(
        investor.locationDetails?.countryId
      )?.name;
      beneficialOwner.address.state = investor.locationDetails?.stateName;
      beneficialOwner.address.city = investor.locationDetails?.cityName;
      beneficialOwner.address.street = investor.locationDetails?.streetName;
      beneficialOwner.address.street2 = investor.locationDetails?.street2Name;
      beneficialOwner.address.postalCode = investor.locationDetails?.postalCode;
      if (!!investor.contact?.phone) {
        const parsedPhoneNumber = parse(investor.contact.phone);
        beneficialOwner.phone = UnitPhone.fromFormValue(
          `+${getCountryCallingCode(parsedPhoneNumber.country)} ${parsedPhoneNumber.phone
          }`
        );
      }

      return beneficialOwner;
    });

    this.beneficialOwnersFormArray.clear();
    beneficialOwners.forEach((beneficialOwner) => {
      this.addBeneficialOwner(beneficialOwner);
    });
  }

  /**
   * Populate beneficial owners from holdings' investors' ownership
   */
  private autoPopulateBeneficialOwners() {
    this.pageForm
      .get('holdingId')
      .valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((holdingId) => !!holdingId),
        tap((_) => this.isLoadingBeneficialOwners$.next(true)),
        switchMap((holdingId) => {
          if (this.autoGeneratedBeneficialOwnersPerHolding.has(+holdingId)) {
            return of(
              this.autoGeneratedBeneficialOwnersPerHolding.get(+holdingId)
            );
          }

          return this.bankApplicationDataService.getAutoGeneratedBeneficialOwners(
            holdingId
          );
        }),
        tap((investorsOwnership) => {
          if (
            !this.autoGeneratedBeneficialOwnersPerHolding.has(
              +this.pageForm.get('holdingId').value
            )
          ) {
            this.autoGeneratedBeneficialOwnersPerHolding.set(
              +this.pageForm.get('holdingId').value,
              investorsOwnership
            );
          }

          this.addBeneficialOwnerFromInvestorOwnership(investorsOwnership);
          this.isLoadingBeneficialOwners$.next(false);
        }),
        catchError((err) => {
          this.isLoadingBeneficialOwners$.next(false);
          return EMPTY;
        })
      )
      .subscribe();
  }

  disableSsnRadio(
    control: AbstractControl,
    formGroup: UntypedFormGroup = null
  ) {
    return (
      control?.value &&
      (!!formGroup
        ? this.mustFillBeneficialOwnerPassport(formGroup)
        : this.mustFillOfficerPassport())
    );
  }

  disablePassportRadio(
    control: AbstractControl,
    formGroup: UntypedFormGroup = null
  ) {
    return (
      control?.value &&
      (!!formGroup
        ? !this.mustFillBeneficialOwnerPassport(formGroup)
        : !this.mustFillOfficerPassport())
    );
  }

  generateUsCitizenshipTooltipMessage() {
    return `SSN is required for US citizens`;
  }

  private mergeFullApplicationData(model: UnitBusinessApplicationReqRes) {
    model.officer.ssn =
      UnitCreateEditApplicationComponent.fullAppData.officer.ssn;
    model.beneficialOwners.forEach((beneficiary, index) => {
      beneficiary.ssn =
        UnitCreateEditApplicationComponent.fullAppData.beneficialOwners[
          index
        ]?.ssn;
    });

    UnitCreateEditApplicationComponent.fullAppData = null;
    return model;
  }

  private cleanUpSsnPassportInForm() {
    const officerSsnOrPassport = this.pageForm.get(
      `officer.ssnOrPassport`
    )?.value;
    if (officerSsnOrPassport === SsnOrPassport.SSN) {
      this.pageForm.get(`officer.passport`)?.reset();
    } else {
      this.pageForm.get(`officer.ssn`)?.reset();
    }

    const beneficialOwners = this.pageForm.get(
      `beneficialOwners`
    ) as UntypedFormArray;
    if (beneficialOwners) {
      beneficialOwners.controls.forEach((bo) => {
        const boSsnOrPassport = bo.get(`ssnOrPassport`)?.value;
        if (boSsnOrPassport === SsnOrPassport.SSN) {
          bo.get('passport').reset();
        } else {
          bo.get('ssn').reset();
        }
      });
    }
  }
}
